import React from "react";
import { Controller, Control, RegisterOptions, useFormContext } from "react-hook-form";
import {
  FormControl,
  TextField,
  FormHelperText,
  Popper,
} from "@material-ui/core";
import styled from "styled-components";
import { Autocomplete, createFilterOptions } from "@material-ui/lab";
import { FontStyles, Tag } from "../../index";
import { makeStyles } from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";
import { fonts } from "../../../config";

const StyledInput = styled(TextField)(({
  theme,
  $inputStyle,
  $alternativecolors,
  $error,
}: any) => {
  const { spacing, palette: colors } = theme;
  return {
    ...FontStyles.medium14,
    padding: theme.spacing(1.962, 2.5),
    alignItems: "flex-start",
    paddingTop: spacing(2.5),
    textOverflow: "ellipsis",
    color: $error ? "red" : theme.palette.text.primary,
    backgroundColor: theme.palette.primary.contrastText,
    borderBottom: "0px",
    borderRadius: 21,
    "& .MuiInputBase-input": {
      padding: 0,
      backgroundColor: "transparent",
      boxShadow: "transparent 0px 0px !important",
      minWidth: "0px!important",
    },
    "& .MuiInput-input": {
      color: $alternativecolors ? colors.text.secondary : colors.text.primary,
      fontFamily: fonts.medium,
      fontSize: fonts.sizes.regular,
      "&::placeholder": { opacity: 0.8 },
    },
    "& .MuiIconButton-root": { color: colors.text.primary },
    ...$inputStyle,
  };
});

const Label = styled.p(({ theme, $witherror, white }: any) => {
  const { palette: colors } = theme;
  return {
    ...FontStyles.semibold14,
    textTransform: "capitalize",
    padding: 0,
    margin: 0,
    marginBottom: "2vh",
    transition: ".2s",
    pointerEvents: "none",
    alignItems: "center",
    display: "flex",
    overflow: "hidden",
    color: $witherror
      ? colors.error.main
      : white
      ? "white"
      : colors.action.selected,
  };
});

const ErrorMessage = styled(FormHelperText)(({ theme }: any) => {
  const { palette: colors } = theme;
  return { color: colors.text.secondary };
});

const PopperSelect = styled(Popper)(({ theme }: any) => {
  const { palette: colors } = theme;
  return {
    "& .MuiAutocomplete-listbox": {
      color: colors.text.primary,
      backgroundColor: colors.primary.contrastText,
      fontFamily: fonts.medium,
      fontSize: fonts.sizes.regular,
      "& li:hover": { backgroundColor: colors.action.focus + 20 },
      '& [aria-selected="true"]': {
        backgroundColor: colors.primary.contrastText + 40,
        borderColor: "transparent",
      },
      "::-webkit-scrollbar-thumb": {
        background: colors.primary.contrastText,
        borderRadius: "15px",
        height: "2px",
      },
    },
  };
});

const LabelView = styled.div(() => ({
  display: "flex",
  alignContent: "center",
  justifyContent: "flex-start",
}));

const useStyles = makeStyles((theme) => ({
  root: {},
  inputRoot: {
    '&.MuiAutocomplete-inputRoot[class*="MuiInput-root"]': {
      paddingBlock: theme.spacing(1.0625),
      paddingInline: theme.spacing(2.5),
      margin: 0,
      "&::before": { display: "none" },
      "&::after": { display: "none" },
    },
  },
}));

export interface InputTagsRHFProps {
  control: Control<any>;
  name: string;
  label?: string;
  options: Array<{ label: string; value: string | number }>;
  multiple?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  rules?: RegisterOptions;
  onChangeCustom?: (value: any) => void;
  placeholder?: string;
  white?: boolean;
  noOptionsText?: boolean;
}

function InputTagsRHF({
  control,
  name,
  label,
  options,
  multiple = false,
  disabled,
  readOnly,
  rules,
  onChangeCustom,
  placeholder = "",
  white,
  noOptionsText,
}: InputTagsRHFProps) {
  const formContext = useFormContext();
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [all, setAll] = React.useState(false);

  const filterOptionsDefault = createFilterOptions({
    stringify: (option: any) => option?.label,
  });

  const RemoveDuplicates = (list: any[]) => {
    const valueArr = list.map((item) => item?.value);
    const isDuplicate = valueArr.some(
      (item, idx) => valueArr.indexOf(item) !== idx
    );
    if (!isDuplicate) return list;
    const toRemove = list
      .map((e) => e["value"])
      .map((e, i, final) => (final.indexOf(e) !== i ? i : false))
      .filter((obj): obj is number => obj !== false)
      .map((e) => list[e]["value"]);
    return list.filter((el) => el?.value !== toRemove[0]);
  };

  function removeAllValue(array: any[]) {
    return array.filter((item) => item?.value !== "all");
  }

  const handleOptions = (opts: any[], isMultiple: boolean) => {
    if (isMultiple) return [{ label: "Todos", value: "all" }, ...opts];
    return [...opts];
  };

  const AutocompleteCustom = styled(Autocomplete)(() => ({}));

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => {
        const errorMsg = fieldState.error?.message;
        const value: any[] = Array.isArray(field.value) ? field.value : [];

        const autocompleteValue = multiple
          ? value.length > 0
            ? value
            : []
          : value.length > 0
          ? value[0]
          : null;

        return (
          <FormControl fullWidth error={Boolean(errorMsg)}>
            <AutocompleteCustom
              filterOptions={filterOptionsDefault}
              noOptionsText={noOptionsText}
              componentsProps={{ disableUnderline: true } as any}
              classes={classes}
              value={autocompleteValue}
              multiple={multiple}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => {
                setOpen(false);
                field.onBlur();
                formContext?.trigger(name);
              }}
              popupIcon={
                <ExpandMore
                  style={{ color: "#4D585A", paddingRight: "20%" }}
                />
              }
              forcePopupIcon={true}
              id={`tags-${name}`}
              options={handleOptions(options, multiple)}
              disabled={readOnly || disabled}
              disableClearable
              clearOnBlur={false}
              clearOnEscape={false}
              renderOption={(option: any) => {
                const isSelected = value?.find(
                  (i) => i?.value === option?.value
                );
                return (
                  <React.Fragment>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          backgroundColor: "#FFFFFF00",
                          border: "2px solid #24272A",
                          borderRadius: "5px",
                          height: "18px",
                          width: "18px",
                          marginRight: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: isSelected
                              ? "#24272A"
                              : "transparent",
                            borderRadius: "2px",
                            height: "8px",
                            width: "8px",
                          }}
                        />
                      </div>
                      {option?.label}
                    </div>
                  </React.Fragment>
                );
              }}
              PopperComponent={(props: any) => <PopperSelect {...props} />}
              getOptionLabel={(option: any) => option?.label || ""}
              onChange={(event: any, selectedValue: any) => {
                let newValue: any[];
                if (multiple) {
                  const haveAll = RemoveDuplicates(
                    selectedValue as any[]
                  )?.find((i) => i?.value === "all");
                  if (all && !haveAll) {
                    setAll(false);
                    newValue = [];
                  } else if (
                    selectedValue === null ||
                    (selectedValue as any[])?.length < 1
                  ) {
                    newValue = RemoveDuplicates(selectedValue as any[]);
                  } else if (haveAll && !all) {
                    setAll(true);
                    newValue = RemoveDuplicates([
                      { value: "all", label: "Todos" },
                      ...options,
                    ]);
                  } else {
                    setAll(false);
                    newValue = RemoveDuplicates(
                      removeAllValue(selectedValue as any[])
                    );
                  }
                } else {
                  newValue = selectedValue ? [selectedValue] : [];
                  setOpen(false);
                }
                field.onChange(newValue);
                if (onChangeCustom) onChangeCustom(newValue);
                if (fieldState.invalid) formContext?.trigger(name);
              }}
              renderInput={(params) => (
                <>
                  <LabelView>
                    <Label
                      white={white}
                      $witherror={Boolean(errorMsg) ? true : undefined}
                    >
                      {label}
                    </Label>
                  </LabelView>
                  <StyledInput
                    placeholder={placeholder}
                    InputProps={{
                      disableUnderline: true,
                      underline: "none",
                    } as any}
                    style={{ height: 50 }}
                    $error={Boolean(errorMsg)}
                    error={Boolean(errorMsg)}
                    {...params}
                  />
                </>
              )}
              renderTags={(values: any, getTagProps: any) => {
                const clearValues = RemoveDuplicates(values);
                const valuesToShow = multiple
                  ? clearValues
                  : values?.length > 0
                  ? [values[0]]
                  : [];

                if (!valuesToShow || valuesToShow.length === 0) return null;

                const selected: any[] = [];
                for (let i = 0; i < valuesToShow.length; i++) {
                  if (
                    Boolean(valuesToShow[i]) &&
                    valuesToShow[i]?.value !== "all"
                  ) {
                    selected.push(
                      <Tag
                        key={valuesToShow[i]?.value}
                        tabindex={valuesToShow[i]?.value}
                        label={valuesToShow[i]?.label}
                        {...getTagProps({ index: i })}
                      />
                    );
                  }
                }
                return (
                  <div
                    style={{
                      overflowY: "auto",
                      height: "4vh",
                      display: "block",
                    }}
                  >
                    {selected}
                  </div>
                );
              }}
            />
            {Boolean(errorMsg) && (
              <ErrorMessage error={true}>{errorMsg}</ErrorMessage>
            )}
          </FormControl>
        );
      }}
    />
  );
}

export default InputTagsRHF;
